import * as client from 'src/internal/metrics/metrics.client';

import type {
  GaugeParams,
  IMetrics,
  MetricName,
  MetricParams,
  MetricTags,
  ValidatedMetricName,
} from 'src/internal/metrics/types';
import type {ObservabilityConfig} from 'src/types';

// TODO: fix references to example files

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-observability/api/Metrics
 */
export default class Metrics implements IMetrics {
  #config: ObservabilityConfig;

  constructor(
    /** Shared context (project, page, service) for scoping events. */
    config: ObservabilityConfig,
  ) {
    this.#config = config;
  }

  increment<TMetricName extends MetricName>(
    metric: TMetricName,
    tags?: MetricTags<TMetricName>,
  ): void;

  increment<TMetricName extends string>(
    metric: ValidatedMetricName<TMetricName>,
    tags?: MetricTags<TMetricName>,
  ): void;

  increment(metric: string, tags?: unknown): void {
    client.increment(
      this.#config,
      metric as MetricName,
      tags as MetricTags<MetricName>,
    );
  }

  gauge<TMetricName extends MetricName>(
    metric: TMetricName,
    measurements: GaugeParams,
    tags?: MetricTags<TMetricName>,
  ): void;

  gauge<TMetricName extends string>(
    metric: ValidatedMetricName<TMetricName>,
    measurements: GaugeParams,
    tags?: MetricTags<TMetricName>,
  ): void;

  gauge(metric: string, measurements: GaugeParams, tags?: unknown): void {
    client.gauge(
      this.#config,
      metric as MetricName,
      measurements,
      tags as MetricTags<MetricName>,
    );
  }

  set<TMetricName extends MetricName>(
    metric: TMetricName,
    values: MetricParams,
    tags?: MetricTags<TMetricName>,
  ): void;

  set<TMetricName extends string>(
    metric: ValidatedMetricName<TMetricName>,
    values: MetricParams,
    tags?: MetricTags<TMetricName>,
  ): void;

  set(metric: string, values: MetricParams, tags?: unknown): void {
    client.set(
      this.#config,
      metric as MetricName,
      values,
      tags as MetricTags<MetricName>,
    );
  }
}
